import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { TertiaryButtonMedium } from "../Buttons/Buttons";
import { DropDownWrapper } from "../FilterBy/filter_by.utils";
import { FilterStates } from "../FilterModal/FilterModal";
import type { StatusColor } from "../Status/Status";

const FilterButton = styled(TertiaryButtonMedium)`
  padding: ${({ theme }) => theme.primitiveTokens.spacing.spacing_xs}
    ${({ theme }) => theme.primitiveTokens.spacing.spacing_md};
  font-size: ${({ theme }) => theme.primitiveTokens.fontSizes.font_size_sm};
  font-weight: ${({ theme }) =>
    theme.primitiveTokens.fontWeights.font_weight_light};
`;

const DropDownModalWrapper = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 100%;
  z-index: 100;
`;

export type FiltersDropDownItem<T = string> = {
  color?: StatusColor;
  text: string;
  value: T;
};

export interface FiltersDropDownProps<T = string> {
  activeItems: FiltersDropDownItem<T>[];
  applyStatus: (items: FiltersDropDownItem<T>[]) => void;
  list: FiltersDropDownItem<T>[];
  header?: string;
  is_status?: boolean;
}

export function FiltersDropDown<T = string>(props: FiltersDropDownProps<T>) {
  const [showDropDown, setShowDropDown] = useState(false);
  const [activeItems, setActiveItems] = useState<FiltersDropDownItem<T>[]>([]);
  const wrapperRef = useRef(document.createElement("div"));
  const { t } = useTranslation();
  useEffect(() => {
    // setActive(Number(activeItem) ? Number(activeItem) : activeItem);
    setShowDropDown(false);
    // props.clickHandler(Number(activeItem) ? Number(activeItem) : activeItem);
  }, []); // activeItem

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, false);
    return () => {
      document.removeEventListener("click", handleClickOutside, false);
    };
  }, []);

  const handleClickOutside = (event: any) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setShowDropDown(false);
    }
  };

  const applyFilters = (items: FiltersDropDownItem<T>[]) => {
    setActiveItems(items);
    setShowDropDown(false);
    props.applyStatus(items);
  };

  const getStatusText = (is_status = true): string => {
    if (activeItems.length === 0) {
      return is_status ? t("Status is any") : props.header || "";
    }

    const firstItemText = activeItems[0].text;
    const remainingCount = activeItems.length - 1;
    const hasMoreItems = remainingCount > 0;
    const moreText = hasMoreItems ? ` + ${remainingCount} ${t("more")}` : "";

    if (is_status) {
      return `${t("Status is")} ${firstItemText}${moreText}`;
    }

    return `${firstItemText}${moreText}`;
  };

  useEffect(() => {
    setActiveItems(props.activeItems);
  }, [props.activeItems]);

  return (
    <DropDownWrapper size="small" ref={wrapperRef}>
      <FilterButton
        onClick={() => setShowDropDown(!showDropDown)}
        active={activeItems.length > 0}
      >
        {getStatusText(props.is_status)}
      </FilterButton>
      {showDropDown && (
        <DropDownModalWrapper>
          <FilterStates
            activeItems={activeItems}
            handleApply={applyFilters}
            list={props.list}
            header={props.header || t("Status")}
          />
        </DropDownModalWrapper>
      )}
    </DropDownWrapper>
  );
}
